import { formatMoney } from '@busbook/shared';

/**
 * Presentation helpers. Trip times are always rendered in the *tenant's* timezone,
 * with an explicit label when the viewer's device sits in a different zone
 * (docs/05 "Design guidance") — a passenger must never read a departure in the
 * wrong clock.
 */

export function money(amountCents: number, currency: string, locale?: string): string {
  return formatMoney(amountCents, currency, locale ?? navigator.language ?? 'en-US');
}

export function deviceTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function timezoneDiffers(tenantTimezone: string): boolean {
  return deviceTimezone() !== tenantTimezone;
}

/** Short zone label, e.g. "WAT" or "GMT+1". */
export function timezoneLabel(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(
    date,
  );
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
}

export function formatTime(iso: string | Date, timeZone: string): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatDate(iso: string | Date, timeZone: string): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
}

export function formatDateTime(iso: string | Date, timeZone: string): string {
  return `${formatDate(iso, timeZone)}, ${formatTime(iso, timeZone)}`;
}

/** "2 h 45 m" — durations are read as spans, not clock times. */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} h`;
  return `${hours} h ${mins} m`;
}

/** "09:42" remaining, for the seat-hold countdown. */
export function formatCountdown(msRemaining: number): string {
  const total = Math.max(0, Math.floor(msRemaining / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** ISO date (YYYY-MM-DD) for a Date as seen in a given timezone. */
export function isoDateIn(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function todayIn(timeZone: string): string {
  return isoDateIn(new Date(), timeZone);
}
